import { useState, useMemo, useRef, Fragment } from 'react'
import { useRouter } from 'next/router'
import Link from 'next/link'
import { Combobox, Transition } from '@headlessui/react'
import { useHotkeys } from 'react-hotkeys-hook'
import { HiSearch, HiX } from 'react-icons/hi'
import { BsStars } from 'react-icons/bs'
import UserThumbnail from 'components/UserThumbnail'
import useSearch, { getByQuery } from 'api/hooks/useSearch'
import useDebounce from 'hooks/useDebounce'
import { isValidName } from 'helpers/utilities'
import { useJoyrideStore } from 'store/state/joyrideStore'

export default function GlobalSearch() {
  const [value, setValue] = useState('')

  const debouncedQuery = useDebounce(value, 500)

  const inputRef = useRef<HTMLInputElement>(null)
  const buttonRef = useRef<HTMLButtonElement>(null)

  useHotkeys('/', () => {
    setTimeout(() => {
      inputRef.current?.focus()
    }, 0)
  })

  const router = useRouter()

  const trimExtension = (name: string) => {
    return name.replace(/\.\w+$|\./gm, '')
  }

  const enableQuery = useMemo(
    () =>
      trimExtension(debouncedQuery).length > 0 &&
      trimExtension(debouncedQuery).length <= 27 &&
      debouncedQuery === value &&
      debouncedQuery.match(/^[a-zA-Z0-9\.]+$/gm) !== null,
    [debouncedQuery, value]
  )

  const { data, isLoading, error } = useSearch({
    params: {
      prefix: trimExtension(debouncedQuery).toLowerCase(),
      view: 'thumbnail'
    },
    options: {
      enabled: enableQuery
    }
  })

  const suggestions = data || []

  const showOptions = enableQuery && !isLoading

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const filtered = event.target.value.replace(/[^a-z0-9]/gi, '')
    setValue(filtered)
  }

  const handleSelection = (name: string) => {
    router.push(`/name/${name}`)
    setValue('')

    setTimeout(() => {
      inputRef.current?.blur()
    }, 0)
  }

  const fetchExactMatch = async (query: string) => {
    const results = await getByQuery({
      prefix: trimExtension(query).toLowerCase()
    })

    return results[0]
  }

  const handleBlur = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = event.target.value

    if (!isValidName(inputValue)) {
      return
    }

    try {
      const result =
        data?.find((nfd) => nfd.name === inputValue) || (await fetchExactMatch(inputValue))

      const resultName = result.name

      if (!resultName) {
        return
      }

      handleSelection(resultName)
    } catch (err) {
      console.error(err)
    }
  }

  const runTour = useJoyrideStore((state) => state.runTour)
  const setRunTour = useJoyrideStore((state) => state.setRunTour)

  const handleFocus = () => {
    if (buttonRef.current && value.length > 0) {
      buttonRef.current.click()
    }

    runTour && setRunTour(false)
  }

  const renderOptions = () => {
    if (error) {
      return (
        <div className="cursor-default select-none relative py-2 px-4 text-gray-500">
          <span className="text-red-500">Error:</span> {error.message}
        </div>
      )
    }

    if (suggestions.length === 0) {
      return (
        <Link href={`/mint?q=${value}`}>
          <a className="block py-2 px-4 text-gray-500 relative pr-10">
            No matches found! Click here to mint{' '}
            <span className="text-gray-900 font-semibold">{value}.algo</span>
            <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
              <BsStars className="h-5 w-5 text-gray-300" aria-hidden="true" />
            </div>
          </a>
        </Link>
      )
    }

    return suggestions.map((suggestion) => (
      <Combobox.Option
        key={suggestion.name}
        className={({ active }) =>
          `cursor-pointer select-none py-2 px-4 text-gray-900 truncate ${
            active || suggestion.name === value ? 'bg-gray-100' : 'bg-white'
          }`
        }
        value={suggestion.name}
      >
        <UserThumbnail nfd={suggestion} link={false} padded />
      </Combobox.Option>
    ))
  }

  return (
    <Combobox value={value} onChange={(name) => handleSelection(name)}>
      <div className="relative inset-0 h-full">
        <div className="relative w-full h-full text-left bg-white rounded-md cursor-default focus:outline-none sm:text-sm text-gray-400 focus-within:text-gray-600">
          <div className="absolute inset-y-0 left-0 flex items-center pointer-events-none">
            <HiSearch className="h-6 w-6" aria-hidden="true" />
          </div>
          <Combobox.Input
            ref={inputRef}
            className="block w-full h-full px-8 py-2 border-transparent text-gray-900 placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-0 focus:border-transparent"
            onChange={handleChange}
            onBlur={handleBlur}
            onFocus={handleFocus}
            autoComplete="new-password"
            spellCheck={false}
            placeholder="Find an NFD"
            autoCorrect="off"
            autoCapitalize="off"
            maxLength={32}
          />
          <Combobox.Button ref={buttonRef} className="hidden" />
          {value.length > 0 && (
            <div className="absolute inset-y-0 right-0 flex items-center">
              <button
                type="button"
                className="inline-flex items-center p-1 border border-transparent rounded-full text-gray-400 bg-white hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-700"
                onClick={() => setValue('')}
              >
                <HiX className="h-6 w-6" aria-hidden="true" />
              </button>
            </div>
          )}
        </div>
        <Transition
          as={Fragment}
          leave="transition ease-in duration-100"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          {showOptions ? (
            <Combobox.Options className="fixed top-16 left-1 right-1 sm:absolute sm:w-full py-1 mt-1 divide-y divide-gray-200 overflow-auto overscroll-contain text-base bg-white rounded-md shadow-lg max-h-48 ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm sm:max-w-md sm:max-h-64">
              {renderOptions()}
            </Combobox.Options>
          ) : (
            <div />
          )}
        </Transition>
      </div>
    </Combobox>
  )
}
